import { useCallback, useState } from 'react';

import {
  EmailRequestType,
  MessagingPlatform,
  UserCommunication,
} from '@this+that/models';
import log from 'loglevel';

import { modifyEmail } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import { useUserCommunicationsStore } from '../../../store';

interface UseMoveToFolderParams {
  userEmailAddress: string;
  platform: MessagingPlatform;
}

export const useMoveToFolder = ({
  userEmailAddress,
  platform,
}: UseMoveToFolderParams) => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    removeUserCommunicationByMessageId,
    getUserCommunicationByMessageId,
    insertUserCommunication,
  } = useUserCommunicationsStore();

  const moveToFolder = useCallback(
    async (messageIds: string[], folderId: string) => {
      if (messageIds.length === 0) {
        return;
      }

      const communicationsToMove = messageIds
        .map((messageId) => {
          const userCommunication = getUserCommunicationByMessageId(messageId);
          if (!userCommunication) {
            log.error(
              `UserCommunication with messageId ${messageId} not found`,
            );
            return null;
          }
          return { messageId, communication: userCommunication };
        })
        .filter(
          (
            item,
          ): item is { messageId: string; communication: UserCommunication } =>
            item !== null,
        );

      if (communicationsToMove.length === 0) {
        log.error('No valid communications to move to folder');
        return;
      }

      setIsLoading(true);

      const backupMap = new Map<string, UserCommunication>();

      communicationsToMove.forEach(({ messageId, communication }) => {
        backupMap.set(messageId, communication);
        removeUserCommunicationByMessageId(messageId);
      });

      try {
        const operations = communicationsToMove.map(
          async ({ messageId, communication }) => {
            try {
              await modifyEmail({
                userEmailAddress,
                platform,
                messageId,
                action: EmailRequestType.MOVE_TO_FOLDER,
                folderId,
              });

              return { messageId, success: true };
            } catch (error) {
              log.error(
                `Failed to move email to folder for messageId ${messageId}:`,
                error,
              );
              return { messageId, success: false, error };
            }
          },
        );

        const results = await Promise.all(operations);
        const failed = results.filter((result) => !result.success);

        if (failed.length === 0) {
          log.info(
            communicationsToMove.length === 1
              ? 'Message moved to folder successfully'
              : `${communicationsToMove.length} messages moved to folder successfully`,
          );
        } else {
          failed.forEach(({ messageId }) => {
            const backup = backupMap.get(messageId);
            if (backup) {
              insertUserCommunication(backup);
            }
          });

          log.warn(
            `${failed.length} operations failed out of ${communicationsToMove.length}`,
          );
          log.error(
            failed.length === communicationsToMove.length
              ? 'Failed to move message(s) to folder'
              : `${failed.length} of ${communicationsToMove.length} messages failed to be moved to folder`,
          );
        }
      } catch (error) {
        log.error('Failed to move message(s) to folder:', error);

        communicationsToMove.forEach(({ messageId }) => {
          const backup = backupMap.get(messageId);
          if (backup) {
            insertUserCommunication(backup);
          }
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      userEmailAddress,
      platform,
      getUserCommunicationByMessageId,
      removeUserCommunicationByMessageId,
      insertUserCommunication,
    ],
  );

  return {
    moveToFolder,
    isLoading,
  };
};
